import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "wouter";
import { Award, Check, CheckCircle2, Cpu, FileText, Folder, GraduationCap, ShieldCheck, Sparkles, X, XCircle } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidate,
  useDictamenForExpediente,
  useElementsForStandard,
  useExpediente,
  useGroup,
  useLmsAvancePct,
  useLmsProgress,
  useLmsRoute,
  usePracticalScoresForExpediente,
  usePreopRuns,
  useRulesForStandard,
  useStandard,
  useUsersByRole,
  useEffectiveUser,
} from "@/store/hooks";
import { buildContextForExpediente, evaluateRules } from "@/lib/ai/simulate";

export default function Dictamination() {
  const params = useParams<{ expedienteId: string }>();
  const [, setLocation] = useLocation();
  const exp = useExpediente(params.expedienteId);
  const cand = useCandidate(exp?.candidatoId);
  const standard = useStandard(exp?.estandar);
  const group = useGroup(exp?.grupoId);
  const rules = useRulesForStandard(exp?.estandar);
  const elements = useElementsForStandard(exp?.estandar);
  const practicalScores = usePracticalScoresForExpediente(exp?.id);
  const existing = useDictamenForExpediente(exp?.id);
  const emitirDictamen = useDemoStore((s) => s.emitirDictamen);
  const dictaminadores = useUsersByRole("dictaminador");
  const effective = useEffectiveUser();

  const dictaminador = useMemo(() => {
    if (effective?.roles.includes("dictaminador")) return effective;
    return dictaminadores[0];
  }, [effective, dictaminadores]);

  const [justification, setJustification] = useState("");
  const [override, setOverride] = useState<"competente" | "todavia-no" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing?.fundamento) setJustification(existing.fundamento);
  }, [existing?.fundamento]);

  if (!exp || !cand) {
    return (
      <AppLayout breadcrumb={["Dictaminador", "Expediente"]}>
        <Card><div style={{ padding: 20, textAlign: "center", color: "var(--cc-gray-500)" }}>Expediente no encontrado.</div></Card>
      </AppLayout>
    );
  }

  const pct = exp.evaluacionPractica.criteriosTotales > 0
    ? Math.round((exp.evaluacionPractica.criteriosCompetente / exp.evaluacionPractica.criteriosTotales) * 100)
    : 0;

  const allCriteria = elements.flatMap((e) => e.criterios.map((c) => ({ ...c, elementoNombre: e.titulo })));

  const ruleResults = (() => {
    const ctx = buildContextForExpediente(exp, { standardEstado: standard?.estado, candidato: cand });
    return evaluateRules(rules, ctx);
  })();
  const blockingFailures = ruleResults.filter((r) => !r.passed && r.rule.severidad === "bloqueante");
  const warningFailures = ruleResults.filter((r) => !r.passed && r.rule.severidad === "advertencia");
  const recommended: "competente" | "todavia-no" = blockingFailures.length === 0 ? "competente" : "todavia-no";
  const selected = override ?? recommended;
  const overridingRecommendation = selected !== recommended;

  function handleEmit(resultado: "competente" | "todavia-no") {
    setError(null);
    if (!dictaminador) {
      setError("No hay un dictaminador activo.");
      return;
    }
    if (justification.trim().length < 10) {
      setError("La justificación es obligatoria (mínimo 10 caracteres).");
      return;
    }
    if (resultado !== recommended && justification.trim().length < 30) {
      setError("Al sobreescribir la recomendación, la justificación debe tener al menos 30 caracteres.");
      return;
    }
    const res = emitirDictamen(exp!.id, dictaminador.id, resultado, justification, recommended);
    if (!res.ok) {
      setError(res.error ?? "No se pudo emitir el dictamen.");
      return;
    }
    if (resultado === "competente") {
      setTimeout(() => setLocation(`/dictaminador/certificado/${exp!.id}`), 200);
    }
  }

  const ya = !!existing;
  const wasRevocado = !!existing?.revocacion;

  return (
    <AppLayout breadcrumb={["Dictaminador", `Expediente ${exp.id.toUpperCase()}`]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>
            Expediente {exp.id.toUpperCase()} · {exp.estandar} · {group?.nombre ?? "—"}
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>{cand.nombre}</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>{cand.unidad} · CURP {cand.curp}</div>
        </div>
        <Badge tone={exp.dictamen === "competente" ? "green" : exp.dictamen === "todavia-no-competente" ? "red" : "amber"}>
          {exp.dictamen === "competente" ? "Dictamen: Competente" : exp.dictamen === "todavia-no-competente" ? "Dictamen: Todavía no competente" : "Pendiente de dictaminación"}
        </Badge>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Resumen del expediente</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              <Stat label="Diagnóstico" value={exp.diagnostico.puntaje != null ? `${exp.diagnostico.puntaje}/100` : "—"} />
              <Stat label="Teórico" value={exp.examenTeorico.puntaje != null ? `${exp.examenTeorico.puntaje}/100` : "—"} />
              <Stat label="Práctica" value={`${exp.evaluacionPractica.criteriosCompetente}/${exp.evaluacionPractica.criteriosTotales}`} />
              <Stat label="Evidencias" value={`${exp.evidencias.verificadas}/${exp.evidencias.requeridas}`} />
            </div>
          </Card>

          <Card>
            <SectionTitle>Componentes</SectionTitle>
            <Item icon={FileText} title="Examen teórico" detail={`${exp.examenTeorico.puntaje ?? "—"}/100 · ${exp.examenTeorico.aprobado ? "Aprobado" : "No aprobado"} · ${exp.examenTeorico.fecha}`} />
            <Item icon={Folder} title="Evaluación práctica" detail={`${exp.evaluacionPractica.criteriosCompetente} de ${exp.evaluacionPractica.criteriosTotales} criterios competentes · ${exp.evaluacionPractica.fecha}`} />
            <Item icon={Award} title="Evidencias verificadas" detail={`${exp.evidencias.verificadas} / ${exp.evidencias.requeridas}`} />
          </Card>

          <DictamenLmsCard expedienteId={exp.id} codigo={exp.estandar} />

          <Card>
            <SectionTitle>Criterios prácticos del estándar</SectionTitle>
            {allCriteria.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>El estándar no tiene criterios configurados.</div>
            )}
            <div style={{ maxHeight: 300, overflowY: "auto" }} data-testid="criteria-list">
              {allCriteria.map((c) => {
                const score = practicalScores[c.id];
                const tone = score?.resultado === "competente" ? "green" : score?.resultado === "todavia-no" ? "red" : "gray";
                const label = score?.resultado === "competente" ? "Competente" : score?.resultado === "todavia-no" ? "Todavía no" : "Sin score";
                return (
                  <div key={c.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 4px", borderBottom: "1px solid var(--cc-gray-100)" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>[{c.id}] {c.descripcion}</div>
                      <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>{c.elementoNombre}{score?.evidenciaUrl ? ` · evidencia: ${score.evidenciaUrl}` : ""}</div>
                      {score?.nota && <div style={{ fontSize: 11, color: "var(--cc-gray-600)", marginTop: 2, fontStyle: "italic" }}>"{score.nota}"</div>}
                    </div>
                    <Badge tone={tone as "green" | "red" | "gray"}>{label}</Badge>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <SectionTitle>Resolución del dictaminador</SectionTitle>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <ResolutionPill active={selected === "competente"} tone="green" onClick={() => setOverride("competente")} testId="pill-competente">
                Competente
              </ResolutionPill>
              <ResolutionPill active={selected === "todavia-no"} tone="red" onClick={() => setOverride("todavia-no")} testId="pill-todavia-no">
                Todavía no competente
              </ResolutionPill>
              {override !== null && (
                <button onClick={() => setOverride(null)} data-testid="button-clear-override" style={{ marginLeft: "auto", fontSize: 11, color: "var(--cc-gray-500)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                  Usar recomendación
                </button>
              )}
            </div>
            {overridingRecommendation && (
              <div style={{ padding: 8, background: "var(--cc-amber-bg)", color: "var(--cc-amber)", borderRadius: 6, fontSize: 11, marginBottom: 10 }}>
                Estás sobreescribiendo la recomendación del motor de reglas ({recommended === "competente" ? "Competente" : "Todavía no"}). La justificación debe ser detallada (mín. 30 caracteres).
              </div>
            )}
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Justificación académica de la resolución (obligatoria, mínimo 10 caracteres)..."
              data-testid="textarea-justification"
              style={{ width: "100%", minHeight: 100, padding: 10, border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }}
            />
            {error && (
              <div style={{ marginTop: 8, padding: 8, background: "var(--cc-red-bg)", color: "var(--cc-red)", borderRadius: 6, fontSize: 12 }} data-testid="text-error">{error}</div>
            )}
            <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>
                Firmado por: <strong>{dictaminador?.nombre ?? "—"}</strong>
                {dictaminador?.cedula && <> · Cédula {dictaminador.cedula}</>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {selected === "todavia-no" ? (
                  <PrimaryButton onClick={() => handleEmit("todavia-no")} testId="button-emit-not-yet">
                    <XCircle size={13} /> {ya ? "Re-emitir" : "Emitir"} dictamen: Todavía no competente
                  </PrimaryButton>
                ) : (
                  <PrimaryButton onClick={() => handleEmit("competente")} testId="button-emit-competente">
                    <CheckCircle2 size={13} /> {ya ? "Re-emitir" : "Confirmar"} competente y emitir certificado
                  </PrimaryButton>
                )}
              </div>
            </div>
            {ya && (
              <div style={{ marginTop: 10, padding: 8, background: wasRevocado ? "var(--cc-red-bg)" : "var(--cc-gray-50)", borderRadius: 6, fontSize: 11, color: wasRevocado ? "var(--cc-red)" : "var(--cc-gray-600)" }}>
                {wasRevocado
                  ? `Dictamen revocado el ${existing!.revocacion!.fecha.slice(0, 10)} · Motivo: ${existing!.revocacion!.motivo}`
                  : `Dictamen previo: ${existing!.resultado === "competente" ? "Competente" : "Todavía no"} el ${existing!.fecha.slice(0, 10)}.`}
              </div>
            )}
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card style={{ background: "linear-gradient(180deg, #ecfeff 0%, #e0f2fe 100%)", border: "1px solid #a5f3fc" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Sparkles size={16} color="var(--cc-teal)" />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Recomendación del motor de reglas</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 4 }}>
              {recommended === "competente" ? "COMPETENTE" : "TODAVÍA NO COMPETENTE"}
            </div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.6 }}>
              {blockingFailures.length === 0
                ? `Sin reglas bloqueantes fallando · ${pct}% criterios prácticos · ${exp.examenTeorico.puntaje ?? "—"}/100 teórico.`
                : `${blockingFailures.length} regla(s) bloqueante(s) fallando — no se recomienda emitir.`}
            </div>
          </Card>

          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <ShieldCheck size={14} color="var(--cc-teal)" />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-navy)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Evaluación de reglas ({ruleResults.length})</span>
            </div>
            {ruleResults.length === 0 && <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>Sin reglas configuradas para este estándar.</div>}
            {blockingFailures.length > 0 && (
              <div style={{ padding: 8, background: "var(--cc-red-bg)", color: "var(--cc-red)", borderRadius: 6, fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
                {blockingFailures.length} bloqueante(s) — impiden emisión
              </div>
            )}
            {warningFailures.length > 0 && (
              <div style={{ padding: 8, background: "var(--cc-amber-bg)", color: "var(--cc-amber)", borderRadius: 6, fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
                {warningFailures.length} advertencia(s)
              </div>
            )}
            <div style={{ maxHeight: 260, overflowY: "auto" }} data-testid="rules-eval-list">
              {ruleResults.map((r) => (
                <div key={r.rule.id} style={{ display: "flex", gap: 6, alignItems: "flex-start", padding: "6px 4px", borderBottom: "1px solid var(--cc-gray-100)" }}>
                  {r.passed
                    ? <Check size={12} color="var(--cc-green)" style={{ flexShrink: 0, marginTop: 2 }} />
                    : <X size={12} color={r.rule.severidad === "bloqueante" ? "var(--cc-red)" : "var(--cc-amber)"} style={{ flexShrink: 0, marginTop: 2 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-navy)" }}>[{r.rule.familia}] {r.rule.nombre}</div>
                    <div style={{ fontSize: 10, color: "var(--cc-gray-500)", fontFamily: "ui-monospace, monospace" }}>{r.detalle}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Cpu size={14} color="var(--cc-navy)" />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-navy)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Hallazgos IA</span>
            </div>
            {exp.alertasIA.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>Sin hallazgos.</div>
            ) : (
              <ul style={{ paddingLeft: 18, fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.7 }}>
                {exp.alertasIA.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

function DictamenLmsCard({ expedienteId, codigo }: { expedienteId: string; codigo: string }) {
  const route = useLmsRoute(codigo);
  const progress = useLmsProgress(expedienteId);
  const pct = useLmsAvancePct(expedienteId, codigo);
  const runs = usePreopRuns(expedienteId);
  if (!route) return null;
  const ready = pct >= 80;
  const completed = route.modules
    .filter((m) => progress.completedModuleIds.includes(m.id))
    .map((m) => {
      const atts = progress.attemptsByModule[m.id] ?? [];
      const last = atts[atts.length - 1];
      return { mod: m, ts: last?.ts };
    });
  return (
    <Card testId="dictamen-lms-card">
      <SectionTitle>Formación</SectionTitle>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <GraduationCap size={18} color={ready ? "var(--cc-green)" : "var(--cc-amber)"} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>
            {progress.completedModuleIds.length}/{route.modules.length} módulos completados · {pct}%
          </div>
          <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
            Umbral “Listo para evaluación”: 80%
          </div>
        </div>
        <Badge tone={ready ? "green" : "amber"}>{ready ? "Listo" : "En curso"}</Badge>
      </div>
      {completed.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-500)", textTransform: "uppercase", marginBottom: 4 }}>Módulos cursados</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {completed.map(({ mod: m, ts }) => (
              <div key={m.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--cc-gray-700)", padding: "3px 6px", background: "var(--cc-gray-50, #fafbfc)", borderRadius: 4 }}>
                <span>✓ {m.titulo}</span>
                <span>{ts ? new Date(ts).toLocaleDateString("es-MX") : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {runs.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-500)", textTransform: "uppercase", marginBottom: 4 }}>Simuladores preoperativos</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {[...runs].reverse().map((r) => (
              <div key={r.id} style={{ padding: 6, background: r.aprobado ? "var(--cc-green-bg)" : "var(--cc-amber-bg)", borderRadius: 4, fontSize: 11, color: "var(--cc-gray-700)" }}>
                {new Date(r.fecha).toLocaleString("es-MX")} · {r.completas}/{r.total}
                {r.criticasFaltantes > 0 ? ` · ${r.criticasFaltantes} críticas` : ""} ·{" "}
                <strong>{r.aprobado ? "Aprobado" : "No aprobado"}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: 10, background: "var(--cc-gray-50)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--cc-navy)", marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Item({ icon: Icon, title, detail }: { icon: React.ComponentType<{ size?: number; color?: string }>; title: string; detail: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, borderBottom: "1px solid var(--cc-gray-100)" }}>
      <Icon size={16} color="var(--cc-teal)" />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{title}</div>
        <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>{detail}</div>
      </div>
    </div>
  );
}

function ResolutionPill({ active, tone, onClick, children, testId }: { active: boolean; tone: "green" | "red"; onClick: () => void; children: React.ReactNode; testId?: string }) {
  const colors = tone === "green"
    ? { bg: "var(--cc-green-bg)", fg: "var(--cc-green)", border: "var(--cc-green)" }
    : { bg: "var(--cc-red-bg)", fg: "var(--cc-red)", border: "var(--cc-red)" };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        padding: "8px 14px",
        background: active ? colors.bg : "white",
        color: active ? colors.fg : "var(--cc-gray-600)",
        border: `2px solid ${active ? colors.border : "var(--cc-gray-200)"}`,
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
